import { useState, useEffect } from "react"
import { useTheme } from "@mui/material/styles"
import { AxiosRequestConfig } from "axios"
import { fetchWithToken } from "api/data"
import { useQuery } from "@tanstack/react-query"
import { ObjectEntity } from "api/object/object.pb"
import { default as routes} from "api/routes"
import { default as MapView, MapObject, MapConnection } from "components/ui/mapview"
import { ColorMode } from "@xyflow/react"
import { Title } from "react-admin"
import { useTranslate } from "ra-core"

function parseCoordinates(coordsString: string) {
    if (!coordsString || coordsString.trim() === '') {
        console.warn('Empty coordinates string:', coordsString)
        return null
    }
    
    const parts = coordsString.split(',')
    if (parts.length !== 2) {
        console.warn('Invalid coordinates format:', coordsString)
        return null
    }
    
    const lat = parseFloat(parts[0].trim())
    const lng = parseFloat(parts[1].trim())
    
    if (isNaN(lat) || isNaN(lng)) {
        console.warn('Invalid coordinate numbers:', { lat, lng, original: coordsString })
        return null
    }
    
    return { x: lat, y: lng }
}

function ObjectMap(props: {group: string, kind: string}): JSX.Element  {
    const { group, kind } = props
    const theme = useTheme()
    const translate = useTranslate()
    const appColorMode = theme.palette.mode
    const [colorMode, setColorMode] = useState<ColorMode>(appColorMode)
    const [markers, setMarkers] = useState<MapObject[]>([])
    const [connections, setConnections] = useState<MapConnection[]>([])
    const [objects, setObjects] = useState<string[]>([])
    
    useEffect(() => {
        setColorMode(appColorMode)
    }, [appColorMode])
    
    
    useEffect(() => {
        const config: AxiosRequestConfig = {
            url: routes.objectMap(group),
            method: 'get',
        }
        fetchWithToken(config)
          .then((response) => {
            const objectsData = response?.data?.objects || []
            const connectionsData = response?.data?.connections || []
            const ms = objectsData
                ?.map((m: ObjectEntity) => {
                    const coords = parseCoordinates(m.geoposition)
                    if (!coords) return null // Пропускаем объекты с некорректными координатами
                    
                    return {
                        id: m.id,
                        name: m.name,
                        coords: coords,
                        description: "", // Начальное значение
                    }
                })
                .filter((item: any) => item !== null) // Убираем null значения
                
            setMarkers(ms as MapObject[])
            setConnections(connectionsData as MapConnection[])
            setObjects(objectsData?.map((m: ObjectEntity) => m.id))
          })
          .catch((error) => console.error(error))
    }, [group])

    const { data: sensorDataRaw, refetch } = useQuery({
        queryKey: ['flow_sensordata', group],
        queryFn: () => {
            if (!markers || markers.length === 0) {
                return Promise.resolve([])
            }

            const config: AxiosRequestConfig = {
                url: routes.deviceDataFlow(kind, true, undefined),
                method: 'get',
                params: { "ids":  objects},
            }
            return fetchWithToken(config).then(response => response.data)
        },
        refetchInterval: 60000, //TODO
    })
    
    useEffect(() => {
        if (objects && objects.length > 0) {
            refetch()
        }
    }, [objects, refetch])

    useEffect(() => {
        if (sensorDataRaw && sensorDataRaw.length > 0) {
            setMarkers((prevMarkers: any) =>
                prevMarkers.map((marker: any) => {
                    const sensorData: any | undefined = sensorDataRaw.find((s: any) => s?.object_id === marker.id)
                    return {
                        ...marker,
                        kind: kind,
                        description: sensorData,
                    }
                })
            )
        }
    }, [sensorDataRaw, setMarkers, kind])

    //{ id: 4, name: 'Объект 4', coords: [48.8566, 2.3522] }, // Париж //TODO
    //{ id: 5, name: 'Объект 5', coords: [51.5074, -0.1278] }, // Лондон //TODO

    return (
        <div style={{ width: '100%', height: '100%' }} className={colorMode}>
            <Title title={translate('resources.objects.map')}/>
            <MapView 
                objects={markers} 
                connections={connections}
                center={{ x: 0, y: 0 }}
                zoom={markers.length > 0 ? undefined : 2}
            />
        </div>
    )
}

ObjectMap.defaultProps = {
    kind: "ddt", //TODO
}

export default ObjectMap